import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { motion } from 'framer-motion';
import { FaYoutube, FaPlay, FaBell, FaExternalLinkAlt, FaCode, FaCloud, FaBrain, FaRocket } from 'react-icons/fa';

const CHANNEL_URL = 'https://www.youtube.com/@aetherstackai';
const CHANNEL_NAME = 'AetherStack AI';

const topics = [
    { icon: FaBrain, label: 'AI & Machine Learning', color: 'from-purple-500 to-indigo-600' },
    { icon: FaCloud, label: 'Cloud & DevOps', color: 'from-blue-500 to-cyan-600' },
    { icon: FaCode, label: 'Software Engineering', color: 'from-green-500 to-emerald-600' },
    { icon: FaRocket, label: 'Tech Tutorials', color: 'from-orange-500 to-red-600' },
];

const YouTube = () => {
    const { theme } = useTheme();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <section
            className={`section w-full py-16 transition-colors duration-300 ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'
            }`}
            id="youtube"
        >
            <div className="container max-w-7xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-14"
                >
                    <h2
                        className={`section__title text-4xl font-bold mb-4 transition-colors duration-300 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}
                    >
                        YouTube Channel
                    </h2>
                    <p
                        className={`text-lg max-w-2xl mx-auto ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}
                    >
                        Sharing knowledge on AI, Cloud, and Modern Engineering
                    </p>
                </motion.div>

                {/* Channel Card */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-4xl mx-auto"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <div className="relative group">
                        {/* Glow */}
                        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-red-600 via-red-500 to-orange-500 opacity-0 group-hover:opacity-30 transition-opacity duration-500 blur-xl" />

                        <div
                            className={`relative rounded-2xl overflow-hidden shadow-xl transition-all duration-500 group-hover:shadow-2xl ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border border-gray-700'
                                    : 'bg-white border border-gray-200'
                            }`}
                        >
                            {/* Banner */}
                            <div className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-900 p-10 overflow-hidden">
                                {/* Animated background */}
                                <div className="absolute inset-0">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-1000" />
                                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 group-hover:scale-150 transition-transform duration-1000 delay-100" />
                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border border-white/5 group-hover:scale-110 transition-transform duration-1000 delay-200" />
                                </div>

                                <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6">
                                    {/* Play icon */}
                                    <motion.div
                                        className="w-20 h-20 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-2xl"
                                        animate={isHovered ? { scale: [1, 1.1, 1] } : {}}
                                        transition={{ duration: 0.6, repeat: isHovered ? Infinity : 0, repeatDelay: 1 }}
                                    >
                                        <FaYoutube className="text-4xl text-white" />
                                    </motion.div>

                                    <div className="text-center sm:text-left">
                                        <h3 className="text-3xl font-bold text-white mb-1">
                                            {CHANNEL_NAME}
                                        </h3>
                                        <p className="text-white/70 text-sm font-medium mb-3">
                                            @aetherstackai
                                        </p>
                                        <p className="text-white/80 text-sm max-w-md">
                                            Deep dives into AI, cloud architecture, DevOps practices, and software engineering -- explained simply.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-8">
                                {/* Topics */}
                                <h4
                                    className={`text-sm font-semibold uppercase tracking-wider mb-4 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                    }`}
                                >
                                    Topics Covered
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                                    {topics.map((topic, index) => {
                                        const TopicIcon = topic.icon;
                                        return (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, y: 20 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: 0.2 + index * 0.1 }}
                                                whileHover={{ y: -4, scale: 1.03 }}
                                                className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all duration-300 cursor-default ${
                                                    theme === 'dark'
                                                        ? 'bg-gray-700/50 hover:bg-gray-700'
                                                        : 'bg-gray-50 hover:bg-gray-100'
                                                }`}
                                            >
                                                <div
                                                    className={`w-10 h-10 rounded-lg bg-gradient-to-br ${topic.color} flex items-center justify-center`}
                                                >
                                                    <TopicIcon className="text-white text-sm" />
                                                </div>
                                                <span
                                                    className={`text-xs font-medium text-center leading-tight ${
                                                        theme === 'dark'
                                                            ? 'text-gray-300'
                                                            : 'text-gray-700'
                                                    }`}
                                                >
                                                    {topic.label}
                                                </span>
                                            </motion.div>
                                        );
                                    })}
                                </div>

                                {/* CTA Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <a
                                        href={CHANNEL_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white"
                                    >
                                        <FaPlay className="text-xs" />
                                        Visit Channel
                                        <FaExternalLinkAlt className="text-xs" />
                                    </a>
                                    <a
                                        href={`${CHANNEL_URL}?sub_confirmation=1`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex-1 flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] border-2 ${
                                            theme === 'dark'
                                                ? 'border-red-500/50 text-red-400 hover:bg-red-500/10'
                                                : 'border-red-500/50 text-red-600 hover:bg-red-50'
                                        }`}
                                    >
                                        <FaBell className="text-sm" />
                                        Subscribe
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default YouTube;
